// src/components/leads/forms/CreateLeadForm.tsx
import React, { useState, type ReactNode } from "react";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";

import { FiTag } from "react-icons/fi";

import { getAuth } from "@/api/client";
import {
  useProductsList,
  usePublicProductsList,
} from "@/api/features/products/queries";
import { useUsers } from "@/api/features/users/queries";

/* --------------------------------- Types --------------------------------- */
export type LeadStatus = "Pending" | "Completed" | "Cancelled";
export type LeadSource =
  | "Walk-in"
  | "Referral"
  | "Online"
  | "Advertisement"
  | "Social Media"
  | "Partner"
  | "Phone Inquiry"
  | "Email Campaign"
  | "Other";
export type Priority = "Low" | "Medium" | "High" | "Urgent";

/**
 * Payload that the page hook receives.
 * This must map cleanly to CreateLeadBody (API) later.
 */
export type UpsertLead = {
  firstName: string;
  lastName: string;
  phone: string;
  email: string;
  date: string; // yyyy-mm-dd
  products: number[]; // multi-product
  assignedToUserId: number | null;
  status: LeadStatus;
  source?: LeadSource;
  priority?: Priority;
  notes?: string;
  reason?: string;
  visaLeadProductId?: number | null;
  visaDetailsDraft?: {
    countryIso2: string;
    enquiryAt: string;
    docsReceivedAt: string;
    docsSubmittedAt: string;
    docsCollectedAt: string;
    appointmentAt: string;
    invoiceAmount: string;
    receiptAmount: string;
    loginId: string;
    loginPassword: string;
    remark: string;
  };
};

type CreateLeadFormProps = {
  onSubmit: (payload: UpsertLead) => void;
  onCancel?: () => void;
  submitting?: boolean;
  errorMessage?: string;
};

type CreateLeadFormState = UpsertLead & {
  productsOpen: boolean;
};

/* ----------------------------- Local constants ---------------------------- */

const SOURCE_OPTIONS: LeadSource[] = [
  "Walk-in",
  "Referral",
  "Online",
  "Advertisement",
  "Social Media",
  "Partner",
  "Phone Inquiry",
  "Email Campaign",
  "Other",
];

const PRIORITY_OPTIONS: Priority[] = ["Low", "Medium", "High", "Urgent"];

const todayISO = () => {
  const d = new Date();
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

/* ------------------------------ Reusable UI ------------------------------- */

function Input({
  value,
  onChange,
  placeholder,
  type = "text",
  required,
  disabled,
  min,
  step,
}: {
  value?: string | number;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  placeholder?: string;
  type?: string;
  required?: boolean;
  disabled?: boolean;
  min?: number;
  step?: number;
}) {
  return (
    <input
      value={value as any}
      onChange={onChange}
      placeholder={placeholder}
      type={type}
      required={required}
      disabled={disabled}
      min={min}
      step={step}
      className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm outline-none
                 placeholder:text-gray-400 focus:border-sky-500 focus:ring-2 focus:ring-sky-500/20"
    />
  );
}

function UiSelect<T extends string>({
  value,
  onChange,
  options,
  placeholder = "— Select —",
  required,
}: {
  value?: T | "";
  onChange?: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  options: T[];
  placeholder?: string;
  required?: boolean;
}) {
  return (
    <select
      value={value ?? ""}
      onChange={onChange}
      required={required}
      className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm outline-none
                 text-gray-900 focus:border-sky-500 focus:ring-2 focus:ring-sky-500/20"
    >
      <option value="" disabled>
        {placeholder}
      </option>
      {options.map((o) => (
        <option key={o} value={o}>
          {o}
        </option>
      ))}
    </select>
  );
}

/** ID-based select (for assigned user / lead owner) */
function IdSelect({
  value,
  onChange,
  options,
  placeholder = "— Select —",
  required,
  disabled,
}: {
  value: number | null;
  onChange: (id: number | null) => void;
  options: { value: number; label: string }[];
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
}) {
  return (
    <select
      value={value != null ? String(value) : ""}
      onChange={(e) =>
        onChange(e.target.value ? Number(e.target.value) : null)
      }
      required={required}
      disabled={disabled}
      className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm outline-none
                 text-gray-900 focus:border-sky-500 focus:ring-2 focus:ring-sky-500/20 disabled:bg-gray-100"
    >
      <option value="" disabled>
        {placeholder}
      </option>
      {options.map((o) => (
        <option key={o.value} value={o.value}>
          {o.label}
        </option>
      ))}
    </select>
  );
}

function Field({
  label,
  children,
  hint,
  icon,
}: {
  label: string;
  children: ReactNode;
  hint?: string;
  icon?: ReactNode;
}) {
  return (
    <label className="block">
      <div className="mb-1 flex items-center gap-1 text-xs font-medium text-gray-700">
        {icon ? <span className="text-gray-400">{icon}</span> : null}
        <span>{label}</span>
      </div>
      {children}
      {hint ? <div className="mt-1 text-[11px] text-gray-500">{hint}</div> : null}
    </label>
  );
}

function getAuthUserDisplayName(user: any): string {
  const fullName = [user?.firstName, user?.lastName].filter(Boolean).join(" ").trim();
  if (fullName) return fullName;
  if (user?.username) return String(user.username);
  if (user?.email) return String(user.email);
  return "Current user";
}

/* ----------------------------- Main form cmp ------------------------------ */

const CreateLeadForm: React.FC<CreateLeadFormProps> = ({
  onSubmit,
  onCancel,
  submitting = false,
  errorMessage,
}) => {
  const authUser = getAuth().user as any;
  const isAffiliate = String(authUser?.roleName ?? "").trim() === "Affiliate Partner";
  const affiliateOwnerLabel = getAuthUserDisplayName(authUser);

  const adminProductsQuery = useProductsList(
    { page: 1, limit: 100 },
    { enabled: !isAffiliate }
  );
  const affiliateProductsQuery = usePublicProductsList(
    { page: 1, limit: 100 },
    { enabled: isAffiliate }
  );

  const productsPage = isAffiliate
    ? affiliateProductsQuery.data
    : adminProductsQuery.data;
  const productsLoading = isAffiliate
    ? affiliateProductsQuery.isLoading
    : adminProductsQuery.isLoading;
  const productsError = isAffiliate
    ? affiliateProductsQuery.isError
    : adminProductsQuery.isError;

  const productOptions: { id: number; name: string }[] =
    productsPage?.items?.map((p: any) => ({
      id: p.id,
      name: p.name,
    })) ?? [];

  // Users – for lead.assignedToUserId
  const {
    data: usersPage,
    isLoading: usersLoading,
    isError: usersError,
  } = useUsers({ page: 1, limit: 100 }, { enabled: !isAffiliate });

  const ownerOptions: { value: number; label: string }[] =
    usersPage?.items?.map((u: any) => {
      const fullName = [u.firstName, u.lastName].filter(Boolean).join(" ");
      const label = fullName || u.username || u.email;
      return {
        value: u.id,
        label,
      };
    }) ?? [];

  /* ------------ Local form state ------------ */

  const [form, setForm] = useState<CreateLeadFormState>({
    firstName: "",
    lastName: "",
    phone: "",
    email: "",
    date: todayISO(),
    products: [],
    assignedToUserId:
      isAffiliate && Number.isFinite(Number(authUser?.id)) && Number(authUser.id) > 0
        ? Number(authUser.id)
        : null,
    status: "Pending",
    source: "Online",
    priority: "Medium",
    notes: "",
    reason: "",
    visaLeadProductId: null,
    visaDetailsDraft: {
      countryIso2: "",
      enquiryAt: "",
      docsReceivedAt: "",
      docsSubmittedAt: "",
      docsCollectedAt: "",
      appointmentAt: "",
      invoiceAmount: "",
      receiptAmount: "",
      loginId: "",
      loginPassword: "",
      remark: "",
    },
    productsOpen: false,
  });

  const change = (k: keyof UpsertLead, v: any) =>
    setForm((p) => ({ ...p, [k]: v }));

  const toggleProduct = (productId: number) => {
    setForm((prev) => {
      const exists = prev.products.includes(productId);
      return {
        ...prev,
        products: exists
          ? prev.products.filter((id) => id !== productId)
          : [...prev.products, productId],
      };
    });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // Let the page hook handle validation + error messages.
    const payload: UpsertLead = {
      firstName: form.firstName,
      lastName: form.lastName,
      phone: form.phone,
      email: form.email,
      date: form.date,
      products: form.products,
      assignedToUserId: form.assignedToUserId,
      status: form.status,
      source: form.source,
      priority: form.priority,
      notes: form.notes,
      reason: form.reason,
      visaLeadProductId: form.visaLeadProductId ?? null,
      visaDetailsDraft: form.visaDetailsDraft,
    };

    onSubmit(payload);
  };

  const productsDisabled =
    productsLoading || productsError || productOptions.length === 0;
  const ownersDisabled =
    isAffiliate || usersLoading || usersError || ownerOptions.length === 0;

  const selectedProducts = productOptions.filter((p) =>
    form.products.includes(p.id)
  );
  const visaProductOptions = selectedProducts.filter((p) => /visa/i.test(p.name));

  return (
    <form
      onSubmit={handleSubmit}
      className="grid grid-cols-1 gap-3 sm:grid-cols-2"
    >
      {errorMessage ? (
        <div className="sm:col-span-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-800">
          {errorMessage}
        </div>
      ) : null}

      {(productsError || usersError) && (
        <div className="sm:col-span-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
          {productsError && "Failed to load products. "}
          {usersError && "Failed to load users. "}
          You can retry by reloading the page.
        </div>
      )}

      <Field label="First Name">
        <Input
          value={form.firstName}
          onChange={(e) => change("firstName", e.target.value)}
          placeholder="Enter first name"
          required
        />
      </Field>

      <Field label="Last Name">
        <Input
          value={form.lastName}
          onChange={(e) => change("lastName", e.target.value)}
          placeholder="Enter last name"
          required
        />
      </Field>

      <Field label="Mobile Number">
        <PhoneInput
          country="in"
          value={form.phone}
          onChange={(v) => change("phone", `+${v}`)}
          inputClass="!w-full !rounded-lg !border !border-gray-300 !text-sm"
          buttonClass="!border-gray-300"
        />
      </Field>

      <Field label="Email ID">
        <Input
          type="email"
          value={form.email}
          onChange={(e) => change("email", e.target.value)}
          placeholder="name@email.com"
          required
        />
      </Field>

      <Field label="Date of lead generated">
        <Input
          type="date"
          value={form.date}
          onChange={(e) => change("date", e.target.value)}
          required
        />
      </Field>

      <Field label="Lead Owner">
        {isAffiliate ? (
          <Input value={affiliateOwnerLabel} disabled />
        ) : (
          <IdSelect
            value={form.assignedToUserId}
            onChange={(id) => change("assignedToUserId", id)}
            options={ownerOptions}
            placeholder={
              ownersDisabled ? "No users available" : "— Select lead owner —"
            }
            required
            disabled={ownersDisabled || submitting}
          />
        )}
      </Field>

      {/* 🔹 Multi-product selection (dropdown-style, like select) */}
      <div className="sm:col-span-2">
        <Field
          label="Products"
          icon={<FiTag />}
          hint="Select one or more products for this lead."
        >
          <div className="relative">
            <button
              type="button"
              className="flex min-h-[38px] w-full items-center justify-between rounded-md border border-gray-300 bg-white px-2 py-1 text-left text-sm hover:border-gray-400"
              onClick={() =>
                setForm((prev) => ({
                  ...prev,
                  productsOpen: !prev.productsOpen,
                }))
              }
              disabled={productsDisabled || submitting}
            >
              <span className="truncate text-gray-700">
                {productsLoading
                  ? "Loading products…"
                  : productsError
                  ? "Failed to load products"
                  : productOptions.length === 0
                  ? "No products found"
                  : form.products.length === 0
                  ? "— Select products —"
                  : selectedProducts.map((p) => p.name).join(", ")}
              </span>
              <span className="ml-2 text-xs text-gray-400">▼</span>
            </button>

            {form.productsOpen &&
              !productsLoading &&
              !productsError &&
              !productsDisabled && (
                <div className="absolute z-20 mt-1 max-h-48 w-full overflow-y-auto rounded-md border border-gray-200 bg-white shadow">
                  {productOptions.length === 0 ? (
                    <div className="px-2 py-1 text-xs text-gray-400">
                      No products found. You can create products first in the
                      Products module.
                    </div>
                  ) : (
                    productOptions.map((p) => {
                      const selected = form.products.includes(p.id);
                      return (
                        <div
                          key={p.id ?? p.name}
                          onClick={() => toggleProduct(p.id)}
                          className={`flex cursor-pointer items-center justify-between px-2 py-1 text-sm hover:bg-gray-100 ${
                            selected ? "bg-sky-50 text-sky-600" : ""
                          }`}
                        >
                          <span className="truncate">{p.name}</span>
                          {selected && (
                            <span className="text-[11px] font-medium">✔</span>
                          )}
                        </div>
                      );
                    })
                  )}
                </div>
              )}
          </div>

          {form.products.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-1">
              {selectedProducts.map((p) => (
                <span
                  key={p.id}
                  className="flex items-center gap-1 rounded-full bg-sky-100 px-2 py-0.5 text-xs text-sky-700"
                >
                  {p.name}
                </span>
              ))}
            </div>
          )}
        </Field>
      </div>

      {visaProductOptions.length > 0 ? (
        <div className="sm:col-span-2 rounded-lg border border-gray-200 p-3">
          <div className="mb-2 text-sm font-semibold text-gray-900">Visa Details</div>
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            <Field label="Visa Product Line">
              <select
                value={form.visaLeadProductId != null ? String(form.visaLeadProductId) : ""}
                onChange={(e) =>
                  setForm((p) => ({
                    ...p,
                    visaLeadProductId: e.target.value ? Number(e.target.value) : null,
                  }))
                }
                className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm outline-none"
              >
                <option value="">— Select visa product —</option>
                {visaProductOptions.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </select>
            </Field>

            <Field label="Country ISO2">
              <Input
                value={form.visaDetailsDraft?.countryIso2 || ""}
                onChange={(e) =>
                  setForm((p) => ({
                    ...p,
                    visaDetailsDraft: {
                      ...(p.visaDetailsDraft as any),
                      countryIso2: e.target.value.toUpperCase(),
                    },
                  }))
                }
                placeholder="CA"
              />
            </Field>
            <Field label="Enquiry"><Input type="date" value={form.visaDetailsDraft?.enquiryAt || ""} onChange={(e)=>setForm((p)=>({...p,visaDetailsDraft:{...(p.visaDetailsDraft as any),enquiryAt:e.target.value}}))} /></Field>
            <Field label="Doc Received"><Input type="date" value={form.visaDetailsDraft?.docsReceivedAt || ""} onChange={(e)=>setForm((p)=>({...p,visaDetailsDraft:{...(p.visaDetailsDraft as any),docsReceivedAt:e.target.value}}))} /></Field>
            <Field label="Doc Submission"><Input type="date" value={form.visaDetailsDraft?.docsSubmittedAt || ""} onChange={(e)=>setForm((p)=>({...p,visaDetailsDraft:{...(p.visaDetailsDraft as any),docsSubmittedAt:e.target.value}}))} /></Field>
            <Field label="Doc Collection"><Input type="date" value={form.visaDetailsDraft?.docsCollectedAt || ""} onChange={(e)=>setForm((p)=>({...p,visaDetailsDraft:{...(p.visaDetailsDraft as any),docsCollectedAt:e.target.value}}))} /></Field>
            <Field label="Appointment"><Input type="date" value={form.visaDetailsDraft?.appointmentAt || ""} onChange={(e)=>setForm((p)=>({...p,visaDetailsDraft:{...(p.visaDetailsDraft as any),appointmentAt:e.target.value}}))} /></Field>
            <Field label="Invoice Amount"><Input type="number" step={0.01} min={0} value={form.visaDetailsDraft?.invoiceAmount || ""} onChange={(e)=>setForm((p)=>({...p,visaDetailsDraft:{...(p.visaDetailsDraft as any),invoiceAmount:e.target.value}}))} placeholder="Enter amount" /></Field>
            <Field label="Receipt Amount"><Input type="number" step={0.01} min={0} value={form.visaDetailsDraft?.receiptAmount || ""} onChange={(e)=>setForm((p)=>({...p,visaDetailsDraft:{...(p.visaDetailsDraft as any),receiptAmount:e.target.value}}))} placeholder="Enter amount" /></Field>
            <Field label="Login ID"><Input value={form.visaDetailsDraft?.loginId || ""} onChange={(e)=>setForm((p)=>({...p,visaDetailsDraft:{...(p.visaDetailsDraft as any),loginId:e.target.value}}))} /></Field>
            <Field label="Login Password"><Input value={form.visaDetailsDraft?.loginPassword || ""} onChange={(e)=>setForm((p)=>({...p,visaDetailsDraft:{...(p.visaDetailsDraft as any),loginPassword:e.target.value}}))} /></Field>
            <div className="sm:col-span-2">
              <Field label="Remark">
                <Input
                  value={form.visaDetailsDraft?.remark || ""}
                  onChange={(e)=>setForm((p)=>({...p,visaDetailsDraft:{...(p.visaDetailsDraft as any),remark:e.target.value}}))}
                  placeholder="Visa remark"
                />
              </Field>
            </div>
          </div>
        </div>
      ) : null}

      {/* Status — locked to Pending on create */}
      <Field label="Status">
        <UiSelect
          value={form.status}
          onChange={() => {
            /* locked to Pending */
          }}
          options={["Pending"]}
          required
        />
      </Field>

      <Field label="Source">
        <UiSelect
          value={form.source || ""}
          onChange={(e) => change("source", e.target.value as LeadSource)}
          options={SOURCE_OPTIONS}
          placeholder="— Select source —"
        />
      </Field>

      <Field label="Priority">
        <UiSelect
          value={form.priority || ""}
          onChange={(e) => change("priority", e.target.value as Priority)}
          options={PRIORITY_OPTIONS}
          placeholder="— Select priority —"
        />
      </Field>

      <div className="sm:col-span-2">
        <Field label="Notes">
          <Input
            value={form.notes || ""}
            onChange={(e) => change("notes", e.target.value)}
            placeholder="Additional notes (optional)"
          />
        </Field>
      </div>

      <div className="sm:col-span-2 mt-2 flex justify-end gap-2">
        {onCancel && (
          <button
            type="button"
            className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
            onClick={onCancel}
            disabled={submitting}
          >
            Cancel
          </button>
        )}
        <button
          type="submit"
          className="rounded-lg bg-sky-600 px-3 py-2 text-sm font-semibold text-white hover:bg-sky-700 disabled:opacity-60"
          disabled={submitting}
        >
          {submitting ? "Saving…" : "Add Lead"}
        </button>
      </div>
    </form>
  );
};

export default CreateLeadForm;
